import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {ContextualMenuToggleOption} from '@Components/contextual-menu/components/contextual-menu-toggle-option';
import {Tag, TagType} from '@Components/tag';
import type {RGBA} from '@Utils/color.util';
import {Tooltip, TOOLTIP_POSITION} from '@Components/tooltip';
import {Text, TextSize} from '@Components/text';
import {BaseTextStrokeColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-text-stroke-color-option';
import {BaseTextStrokeWidthOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-text-stroke-width-option';
import styles from './base-text-outline-option.module.scss';

interface BaseTextOutlineOptionProps {
  stroke: boolean;
  strokeColor: RGBA;
  strokeWidth: number;
  isDisabled: boolean;
  onClickColorFill(): void;
  onToggleOutlineOption(): void;
  updateStrokeThickness(strokeWidth: number, undoable?: boolean): void;
  showNewTag?: boolean;
  label?: string;
}

export function BaseTextOutlineOption({label = window.i18next.t('pmwjs_outline'), showNewTag = false, ...props}: BaseTextOutlineOptionProps): ReactElement<any> {
  const [showTooltip, setShowTooltip] = useState<boolean>(false);

  const getNewTag = (): ReactElement<any> | undefined => {
    return showNewTag ? <Tag text={window.i18next.t('pmwjs_new')} type={TagType.SECONDARY} /> : undefined;
  };

  const onToggleOutlineOption = (): void => {
    props.onToggleOutlineOption();
  };

  const onClickColorFill = (): void => {
    props.onClickColorFill();
  };

  const getStrokeColorOption = (): ReactElement<any> => {
    return <BaseTextStrokeColorOption strokeColor={props.strokeColor} onClickColorFill={onClickColorFill} />;
  };

  const updateStrokeThickness = (strokeWidth: number, undoable = true): void => {
    props.updateStrokeThickness(strokeWidth, undoable);
  };

  const getStrokeWidthOption = (): ReactElement<any> => {
    return <BaseTextStrokeWidthOption strokeWidth={props.strokeWidth} onUpdateStrokeWidth={updateStrokeThickness} />;
  };

  const getSubOptions = (): ReactElement<any> | null => {
    if (!props.stroke) {
      return null;
    }

    return (
      <>
        {getStrokeColorOption()}
        {getStrokeWidthOption()}
      </>
    );
  };

  return (
    <div className={styles.container}>
      <ContextualMenuToggleOption
        tag={getNewTag()}
        id="item-outline-option"
        label={label}
        isChecked={props.stroke}
        isDisabled={props.isDisabled}
        onClick={onToggleOutlineOption}
        onMouseEnter={(): void => {
          if (!showTooltip) {
            setShowTooltip(true);
          }
        }}
        onMouseLeave={(): void => {
          if (showTooltip) {
            setShowTooltip(false);
          }
        }}
      >
        {getSubOptions()}
      </ContextualMenuToggleOption>
      {showTooltip && props.isDisabled ? (
        <Tooltip
          className={styles.tooltip}
          position={TOOLTIP_POSITION.BOTTOM_RIGHT}
          id="outline-toggle-disable-tooltip"
          text={<Text val={window.i18next.t('pmwjs_cannot_select_outline')} size={TextSize.XXSMALL} />}
        />
      ) : null}
    </div>
  );
}
